import React, { useState, useEffect } from "react";
import { StaffSideMenu } from "../../staff/StaffSideMenu";
import API_URL from "../../../api/API";
import { useStaffAuthContext } from "../../staff/useStaffAuthContext";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom"; // Import useNavigate
import { CiSearch } from "react-icons/ci";
import { HiOutlinePlusSm } from "react-icons/hi";
import { FiEdit, FiEdit2 } from "react-icons/fi";
import { RiDeleteBin4Line } from "react-icons/ri";
import {
  MdKeyboardArrowDown,
  MdKeyboardArrowLeft,
  MdKeyboardArrowRight,
  MdKeyboardArrowUp,
} from "react-icons/md";

export const AdminManageSpeedTrainingFocusTemplates = () => {
  const { staff } = useStaffAuthContext();
  const navigate = useNavigate(); // Initialize navigation hook

  const [trainingFocuses, setTrainingFocuses] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(75);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [training_focus, setTrainingFocus] = useState("");
  const [training_phase, setTrainingPhase] = useState("");

  const [trainingFocusList, setTrainingFocusList] = useState([]);
  const [trainingPhaseList, setTrainingPhaseList] = useState([]);
  const [useCustomFocus, setUseCustomFocus] = useState(false);
  const [useCustomPhase, setUseCustomPhase] = useState(false);

  const [deletePopup, setDeletePopup] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  const [expandedId, setExpandedId] = useState(null);

  const fetchDropdownLists = async () => {
    try {
      const [focusRes, phaseRes] = await Promise.all([
        fetch(API_URL.ADMIN_SPEED_TEMPLATE_GET_TRAINING_FOCUS_LIST, {
          headers: { Authorization: `Bearer ${staff.token}` },
        }),
        fetch(API_URL.ADMIN_SPEED_TEMPLATE_GET_TRAINING_PHASE_LIST, {
          headers: { Authorization: `Bearer ${staff.token}` },
        }),
      ]);

      const focusData = await focusRes.json();
      const phaseData = await phaseRes.json();

      setTrainingFocusList(focusData.data || []);
      setTrainingPhaseList(phaseData.data || []);
    } catch (err) {
      console.error("Error loading dropdown data", err);
    }
  };

  useEffect(() => {
    if (staff?.token) fetchDropdownLists();
  }, [staff.token]);

  useEffect(() => {
    const fetchTrainingFocuses = async () => {
      setIsLoading(true);
      try {
        const res = await fetch(
          API_URL.ADMIN_SPEED_TEMPLATE_TRAINING_FOCUS_MANAGE,
          {
            headers: {
              Authorization: `Bearer ${staff.token}`,
            },
          },
        );
        const data = await res.json();
        setTrainingFocuses(data.data || []);
        setTotalPages(Math.ceil((data.data || []).length / itemsPerPage));
      } catch (err) {
        setError("Failed to load training focus data.");
      } finally {
        setIsLoading(false);
      }
    };

    if (staff?.token) fetchTrainingFocuses();
  }, [staff.token, currentPage, itemsPerPage]);

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
  };

  const normalizedQuery = searchQuery?.toLowerCase().trim() || "";

  const filteredTrainingFocuses = trainingFocuses.filter((item) => {
    if (!normalizedQuery) return true; // ✅ Show all when search is empty

    const focus = item.training_focus?.toLowerCase() || "";
    const phase = item.training_phase?.toLowerCase() || "";

    return focus.includes(normalizedQuery) || phase.includes(normalizedQuery);
  });

  useEffect(() => {
    setTotalPages(Math.ceil(filteredTrainingFocuses.length / itemsPerPage));
  }, [filteredTrainingFocuses, itemsPerPage]);

  const handleDelete = async () => {
    if (!selectedTemplateId) return;

    try {
      const response = await fetch(
        `${API_URL.ADMIN_DELETE_SPEED_TEMPLATE_TRAINING_FOCUS}${selectedTemplateId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${staff.token}` },
        },
      );

      if (response.ok) {
        setDeletePopup(false);
        setSelectedTemplateId(null);
        alert("Training focus deleted successfully");
        setTrainingFocuses((prev) =>
          prev.filter((item) => item._id !== selectedTemplateId),
        );
      } else {
        alert("Failed to delete template.");
      }
    } catch (error) {
      alert("Error deleting template.");
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleCreate = async () => {
    if (!training_focus || !training_phase) {
      setError("Both fields are required.");
      return;
    }

    try {
      setIsLoading(true);
      setError("");
      const response = await fetch(
        API_URL.ADMIN_POST_SPEED_TEMPLATE_CREATE_TRAINING_FOCUS,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${staff.token}`,
          },
          body: JSON.stringify({
            training_focus: training_focus,
            training_phase: training_phase,
          }),
        },
      );

      const newData = await response.json();
      setTrainingFocuses((prev) => [...prev, newData.data]);
      setShowModal(false);
      setTrainingFocus("");
      setTrainingPhase("");
      setUseCustomFocus(false);
      setUseCustomPhase(false);

      // Redirect to the edit screen for the newly created template
      if (newData?.data._id) {
        navigate(
          `/administrator/template-speed/training-focus/edit/${newData.data._id}`,
        );
      } else {
        await fetchDropdownLists(); // ← Refresh list
      }
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to create training focus.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex md:h-screen font-inter">
      <StaffSideMenu />
      <div className="max-md:w-full md:flex-1 md:flex md:flex-col md:overflow-hidden md:overflow-y-auto bg-bgBase">
        <div className="bg-white space-y-6 pt-5 pb-3 md:py-6 px-5 md:px-10 xl:px-14">
          <div className="w-full max-w-[1336px]">
            <div className="flex flex-col md:justify-between gap-6">
              <h1 className="font-inter font-medium text-dark text-xl md:text-2xl xl:text-[28px] !leading-none max-md:pl-12">
                Training Workouts
              </h1>
              <div className="flex flex-wrap md:justify-between md:items-center gap-3">
                <div className="relative w-full min-w-64 max-w-64">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={handleSearch}
                    placeholder="Search warmups..."
                    className="w-full p-3 border border-black/10 rounded-lg font-inter font-normal leading-3 text-sm pl-10"
                  />
                  <span className="absolute top-1/2 -translate-y-1/2 left-3">
                    <CiSearch color="#666666" size={20} />
                  </span>
                </div>
                <button
                  onClick={() => setShowModal(true)}
                  className="px-6 py-3 flex items-center gap-2 bg-dark text-white font-inter font-normal text-sm leading-tight rounded-lg hover:bg-black"
                >
                  <HiOutlinePlusSm color="#fff" size={18} /> Create Training Focus
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="py-10 px-5 md:px-8 xl:px-16">
          <div className="w-full max-w-[1336px]">
            <h1 className="text-xl md:text-2xl font-medium mb-4">
              Manage Training Focus
            </h1>
            {error && (
              <div className="bg-red-100 text-red-500 p-3 rounded mb-4">
                {error}
              </div>
            )}
            {isLoading ? (
              <div className="bg-white w-48 rounded-md p-2">
                <p className="text-dark font-normal text-sm">Loading...</p>
              </div>
            ) : (
              <div className="flex-1 overflow-auto">
                <div className="rounded-10 border border-tblBorder w-full overflow-y-auto md:show-scrollbar md:max-h-[550px]">
                  <table className="table-auto w-full bg-white rounded-10">
                    <tbody className="divide-y divide-tblHead px-4 ">
                      {filteredTrainingFocuses
                        .slice(
                          (currentPage - 1) * itemsPerPage,
                          currentPage * itemsPerPage,
                        )
                        .map((item, index) => (
                          <React.Fragment key={item._id}>
                            <tr key={item._id} className={`cursor-pointer`}>
                              <td onClick={() => setExpandedId( expandedId === item._id ? null : item._id, ) }
                                  className="p-4">
                                <div className="flex flex-wrap justify-between gap-x-5 gap-y-2">
                                  <div>
                                    <p className="block text-dark text-sm font-medium leading-tight">
                                      {item.training_focus}
                                    </p>
                                    <p className="block text-slate100 text-xs font-medium leading-tight">
                                      {item.training_phase}
                                    </p>
                                  </div>
                                  <div className="flex justify-end items-center gap-2">
                                    {/* Calculate total distance */}
                                    {item.sprints?.length > 0 && (
                                      <p className="text-slate100 text-sm leading-tight font-medium">
                                        {item.sprints.reduce(
                                          (total, sprint) => {
                                            if (sprint.type === "Rest")
                                              return total;

                                            const sets =
                                              parseFloat(sprint.sets) || 0;
                                            const reps =
                                              parseFloat(sprint.reps) || 0;
                                            const distance =
                                              parseFloat(sprint.distance) || 0;

                                            return (
                                              total + sets * reps * distance
                                            );
                                          },
                                          0,
                                        )}
                                        m
                                      </p>
                                    )}
                                    {/* Add edit/delete open/close btn */}
                                    <Link
                                      to={`/administrator/template-speed/training-focus/edit/${item._id}`}
                                      className="flex-none p-2 rounded hover:bg-gray-200"
                                    >
                                      <FiEdit color="#666666" size={18} />
                                    </Link>

                                    <button
                                      className="p-2 rounded-md hover:bg-gray-200"
                                      onClick={() => {
                                        setSelectedTemplateId(item._id);
                                        setDeletePopup(true);
                                      }}
                                    >
                                      <RiDeleteBin4Line
                                        color="#666666"
                                        size={20}
                                      />
                                    </button>

                                    <button className="p-2 rounded hover:bg-gray-200">
                                      {expandedId === item._id ? (
                                        <>
                                          <MdKeyboardArrowUp
                                            color="#6A7282"
                                            size={20}
                                          />
                                        </>
                                      ) : (
                                        <>
                                          <MdKeyboardArrowDown
                                            color="#6A7282"
                                            size={20}
                                          />
                                        </>
                                      )}
                                    </button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                            {expandedId === item._id && (
                              <tr>
                                <td colSpan="4" className="p-4">
                                  <div className="bg-bgBase p-4 rounded-10">
                                    {/* Calculate total distance */}
                                    {/* {item.sprints?.length > 0 && (
                                      <p className="font-semibold mb-4">
                                        Total Distance:{" "}
                                        {item.sprints.reduce(
                                          (total, sprint) => {
                                            if (sprint.type === "Rest")
                                              return total;

                                            const sets =
                                              parseFloat(sprint.sets) || 0;
                                            const reps =
                                              parseFloat(sprint.reps) || 0;
                                            const distance =
                                              parseFloat(sprint.distance) || 0;

                                            return (
                                              total + sets * reps * distance
                                            );
                                          },
                                          0,
                                        )}{" "}
                                        meters
                                      </p>
                                    )} */}

                                    {item.sprints?.length > 0 ? (
                                      <div className="space-y-4">
                                        {item.sprints.map(
                                          (sprint, sprintIndex) =>
                                            sprint.type === "Rest" ? (
                                              <div
                                                key={`rest-${sprintIndex}`}
                                                className="w-full bg-[#E1E1E1] py-2.5 px-3 rounded-10 text-dark font-normal text-sm"
                                              >
                                                Rest Segment:{" "}
                                                {sprint.rest_notes ||
                                                  "No notes"}
                                              </div>
                                            ) : (
                                              <div
                                                key={`sprint-${sprintIndex}`}
                                                className="bg-white border border-tblBorder shadow rounded-10 space-y-3 p-4"
                                              >
                                                <div className="flex justify-between gap-5">
                                                  <p></p>
                                                  <div className="flex gap-1">
                                                    <p className="text-dark font-medium text-sm">
                                                      {sprint.distance}m
                                                    </p>
                                                    <p className="text-dark font-medium text-sm">
                                                      ·
                                                    </p>
                                                    <p className="text-dark font-medium text-sm">
                                                      {sprint.intensity}%
                                                    </p>
                                                  </div>
                                                </div>
                                                {/* sets reps / pace */}
                                                <div className="flex flex-wrap gap-x-5 gap-y-2">
                                                  <div className="flex items-center gap-1.5">
                                                    <span className="text-slate100 font-normal text-xs">
                                                      Sets:
                                                    </span>{" "}
                                                    <p className="text-dark font-medium text-sm">
                                                      {sprint.sets}
                                                    </p>
                                                  </div>
                                                  <div className="flex items-center gap-1.5">
                                                    <span className="text-slate100 font-normal text-xs">
                                                      Reps:
                                                    </span>{" "}
                                                    <p className="text-dark font-medium text-sm">
                                                      {sprint.reps}
                                                    </p>
                                                  </div>
                                                  <div className="flex items-center gap-1.5">
                                                    <span className="text-slate100 font-normal text-xs">
                                                      Pace:
                                                    </span>{" "}
                                                    <p className="text-dark font-medium text-sm">
                                                      {sprint.pace}
                                                    </p>
                                                  </div>
                                                  {/* <div className="flex items-center gap-1.5">
                                                    <span className="text-slate100 font-normal text-xs">
                                                      Intensity:
                                                    </span>{" "}
                                                    <p className="text-dark font-medium text-sm">
                                                      {sprint.intensity}
                                                    </p>
                                                  </div> */}
                                                  <div className="flex items-center gap-1.5">
                                                    <span className="text-slate100 font-normal text-xs">
                                                      Rest:
                                                    </span>{" "}
                                                    <p className="text-dark font-medium text-sm">
                                                      {sprint.rest}
                                                    </p>
                                                  </div>
                                                  <div className="flex items-center gap-1.5">
                                                    <span className="text-slate100 font-normal text-xs">
                                                      Exercise:
                                                    </span>{" "}
                                                    <p className="text-dark font-medium text-sm">
                                                      {sprint.exercise_id
                                                        ?.title || "None"}
                                                    </p>
                                                  </div>
                                                </div>
                                                <div className="pt-2.5 border-t border-tblBorder flex items-center gap-1.5">
                                                  {/* <span className="text-slate100 font-normal text-xs">Notes:</span>{" "} */}
                                                  <p className="text-slate100 font-normal text-xs">
                                                    {sprint.notes || "-"}
                                                  </p>
                                                </div>
                                              </div>
                                            ),
                                        )}
                                      </div>
                                    ) : (
                                      <p className="text-sm font-normal text-slate100 text-center italic">
                                        No sprint data found.
                                      </p>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-between items-center mt-4 p-4">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                    className={`px-4 py-2 min-w-28 flex justify-center items-center gap-1 rounded-lg text-[#99A1AF] font-inter font-normal text-sm ${
                      currentPage === 1 ? "cursor-not-allowed" : "text-black"
                    }`}
                  >
                    <MdKeyboardArrowLeft size={20} /> Previous
                  </button>
                  <span className="text-dark font-inter font-normal text-sm">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                    className={`px-4 py-2 min-w-28 flex justify-center items-center gap-1 rounded-lg text-[#99A1AF] font-inter font-normal text-sm ${
                      currentPage === totalPages
                        ? "cursor-not-allowed"
                        : "text-black"
                    }`}
                  >
                    Next <MdKeyboardArrowRight size={20} />
                  </button>
                </div>
              </div>
            )}

            {showModal && (
              <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
                  <div className="p-5">
                    <h2 className="text-dark text-xl font-medium">
                      Create Training Focus
                    </h2>
                  </div>

                  <div className="p-5 border-y border-tblBorder space-y-3">
                    {/* Training Focus Dropdown */}
                    {!useCustomFocus ? (
                      <>
                        <select
                          className="w-full border border-tblBorder p-3 rounded-10 placeholder:text-slate100 outline-none font-medium"
                          value={training_focus}
                          onChange={(e) => {
                            if (e.target.value === "__new__") {
                              setTrainingFocus("");
                              setUseCustomFocus(true);
                            } else {
                              setTrainingFocus(e.target.value);
                            }
                          }}
                        >
                          <option value="">Select Training Focus</option>
                          {trainingFocusList.map((focus, idx) => (
                            <option key={idx} value={focus}>
                              {focus}
                            </option>
                          ))}
                          <option value="__new__">Add new...</option>
                        </select>
                      </>
                    ) : (
                      <>
                        <input
                          type="text"
                          placeholder="Enter new training focus"
                          value={training_focus}
                          onChange={(e) => setTrainingFocus(e.target.value)}
                          className="w-full border border-tblBorder p-3 rounded-10 placeholder:text-slate100 outline-none font-medium"
                        />
                        <button
                          onClick={() => setUseCustomFocus(false)}
                          className="text-sm text-blue-600 mb-3 hover:underline"
                        >
                          Select from existing
                        </button>
                      </>
                    )}

                    {/* Training Phase Dropdown */}
                    {!useCustomPhase ? (
                      <>
                        <select
                          className="w-full border border-tblBorder p-3 rounded-10 placeholder:text-slate100 outline-none font-medium"
                          value={training_phase}
                          onChange={(e) => {
                            if (e.target.value === "__new__") {
                              setTrainingPhase("");
                              setUseCustomPhase(true);
                            } else {
                              setTrainingPhase(e.target.value);
                            }
                          }}
                        >
                          <option value="">Select Training Phase</option>
                          {trainingPhaseList.map((phase, idx) => (
                            <option key={idx} value={phase}>
                              {phase}
                            </option>
                          ))}
                          <option value="__new__">Add new...</option>
                        </select>
                      </>
                    ) : (
                      <>
                        <input
                          type="text"
                          placeholder="Enter new training phase"
                          value={training_phase}
                          onChange={(e) => setTrainingPhase(e.target.value)}
                          className="w-full border border-tblBorder p-3 rounded-10 placeholder:text-slate100 outline-none font-medium"
                        />
                        <button
                          onClick={() => setUseCustomPhase(false)}
                          className="text-sm text-blue-600 hover:underline"
                        >
                          Select from existing
                        </button>
                      </>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={() => {
                          setShowModal(false);
                          setUseCustomFocus(false);
                          setUseCustomPhase(false);
                        }}
                        className="bg-transparent border border-tblBorder rounded-10 py-2.5 px-5 text-dark font-inter font-medium text-sm leading-tight capitalize hover:bg-dark hover:text-white transition"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleCreate}
                        className="bg-dark border border-tblBorder rounded-10 py-2.5 px-5 text-white font-inter font-medium text-sm leading-tight capitalize hover:bg-black transition"
                      >
                        Create
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Delete Confirmation Popup */}
            {deletePopup && (
              <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
                  <div className="p-5">
                    <h2 className="text-dark text-xl font-medium">
                      Are you sure?
                    </h2>
                  </div>
                  <div className="p-5 border-y border-tblBorder space-y-3 text-sm font-normal">
                    <p>Do you really want to delete this training focus?</p>
                  </div>
                  <div className="p-5">
                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={() => setDeletePopup(false)}
                        className="bg-transparent border border-tblBorder rounded-10 py-2.5 px-5
                         text-dark font-inter font-medium text-sm leading-tight capitalize
                          hover:bg-dark hover:text-white transition"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleDelete}
                        className="bg-dark border border-tblBorder rounded-10 py-2.5 px-5 text-white
                         font-inter font-medium text-sm leading-tight capitalize hover:bg-black transition"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
